/**
 * Reseller-Optimized Calculator.
 *
 * A floating calculator accessible from any page via a keyboard shortcut
 * (Shift+C) or a header button. Goes beyond basic arithmetic with
 * reseller-specific tools:
 *
 * - Standard: basic arithmetic with history
 * - Margin: cost + margin% → price, or cost + price → margin%
 * - Markup: cost × markup multiplier → price
 * - Profit: sale price − COGS breakdown → net profit
 * - Shipping: distribute shipping across items proportionally
 * - Commission: sale amount × rate → commission + net
 * - ROI: (revenue − cost) / cost → ROI%
 * - Quick Tax: subtotal + tax% → total
 */

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useCalculatorStore } from "@/stores/calculatorStore";
import { apiClient } from "@/api/client";
import {
  X,
  Delete,
  RotateCcw,
  Calculator,
  TrendingUp,
  Percent,
  DollarSign,
  Truck,
  Users,
  BarChart3,
  Receipt,
  ArrowLeftRight,
} from "lucide-react";

type CalcMode =
  | "standard"
  | "margin"
  | "markup"
  | "profit"
  | "shipping"
  | "commission"
  | "roi"
  | "tax"
  | "converter";

interface HistoryEntry {
  mode: string;
  expression: string;
  result: string;
}

interface ResellerCalculatorProps {
  isOpen: boolean;
  onClose: () => void;
}

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(v);

const fmtPct = (v: number) => `${v.toFixed(2)}%`;

interface ModeInfo {
  key: CalcMode;
  label: string;
  icon: React.ElementType;
  description: string;
}

const MODE_GROUPS: { label: string; modes: ModeInfo[] }[] = [
  {
    label: "Arithmetic",
    modes: [
      {
        key: "standard",
        label: "Standard",
        icon: Calculator,
        description: "Basic arithmetic",
      },
      {
        key: "tax",
        label: "Tax",
        icon: Receipt,
        description: "Subtotal + tax% → total",
      },
    ],
  },
  {
    label: "Business",
    modes: [
      {
        key: "margin",
        label: "Margin",
        icon: TrendingUp,
        description: "Cost + margin → price",
      },
      {
        key: "markup",
        label: "Markup",
        icon: Percent,
        description: "Cost × multiplier → price",
      },
      {
        key: "profit",
        label: "Profit",
        icon: DollarSign,
        description: "Sale − COGS → net",
      },
      {
        key: "commission",
        label: "Comm.",
        icon: Users,
        description: "Sale × rate → commission",
      },
      {
        key: "shipping",
        label: "Shipping",
        icon: Truck,
        description: "Split shipping costs",
      },
      {
        key: "roi",
        label: "ROI",
        icon: BarChart3,
        description: "Return on investment",
      },
    ],
  },
  {
    label: "Tools",
    modes: [
      {
        key: "converter",
        label: "Converter",
        icon: ArrowLeftRight,
        description: "Currency & unit conversions",
      },
    ],
  },
];

const ALL_MODES = MODE_GROUPS.flatMap((g) => g.modes);

function ResellerCalculator({ isOpen, onClose }: ResellerCalculatorProps) {
  const [mode, setMode] = useState<CalcMode>("standard");
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [prefill, setPrefill] = useState<Record<string, string>>({});
  const [contextSource, setContextSource] = useState<string | undefined>();
  const [onApplyCallback, setOnApplyCallback] = useState<
    ((value: number) => void) | null
  >(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const addHistory = useCallback((entry: HistoryEntry) => {
    setHistory((prev) => [...prev.slice(-19), entry]);
  }, []);

  /** Apply a value to the triggering field and close the calculator. */
  const handleApply = useCallback(
    (value: number) => {
      if (onApplyCallback) {
        onApplyCallback(value);
        onClose();
      }
    },
    [onApplyCallback, onClose],
  );

  // Consume context from calculator store when opened.
  useEffect(() => {
    if (!isOpen) return;
    const ctx = useCalculatorStore.getState().context;
    if (ctx) {
      setMode(ctx.mode);
      setPrefill(ctx.prefill);
      setContextSource(ctx.source);
      // Store the callback in a wrapper to avoid zustand serialization issues.
      // We use a function-returning-function pattern because setState treats
      // functions specially.
      setOnApplyCallback(ctx.onApply ? () => ctx.onApply! : null);
      useCalculatorStore.getState().clearContext();
    } else {
      setPrefill({});
      setContextSource(undefined);
      setOnApplyCallback(null);
    }
  }, [isOpen]);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  // Close on click outside.
  useEffect(() => {
    if (!isOpen) return;
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-end p-4 pt-16 lg:pt-4">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 backdrop-blur-[2px]" />

      {/* Calculator Panel */}
      <div
        ref={panelRef}
        className="relative z-10 w-[340px] max-h-[calc(100vh-2rem)] overflow-hidden rounded-[var(--radius-xl)] border border-[var(--color-border)] glass-heavy shadow-lg animate-scale-in flex flex-col"
      >
        {/* Header — shows active mode name prominently */}
        <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-2.5">
          <div className="flex items-center gap-2.5">
            {(() => {
              const active = ALL_MODES.find((m) => m.key === mode);
              return active ? (
                <active.icon className="h-4 w-4 text-[var(--color-interactive-primary)]" />
              ) : null;
            })()}
            <div className="leading-none">
              <h3 className="text-sm font-semibold text-[var(--text-primary)]">
                {ALL_MODES.find((m) => m.key === mode)?.label || "Calculator"}
              </h3>
              <p className="text-[10px] text-[var(--text-tertiary)] mt-0.5">
                {contextSource ? (
                  <span className="text-[var(--color-interactive-primary)]">
                    from {contextSource}
                  </span>
                ) : (
                  ALL_MODES.find((m) => m.key === mode)?.description
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setHistory([])}
              className="flex h-6 w-6 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
              title="Clear history"
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={onClose}
              className="flex h-6 w-6 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* Mode Selector — grouped grid, all visible without scrolling */}
        <div className="border-b border-[var(--color-border)] px-3 py-2 space-y-2">
          {MODE_GROUPS.map((group) => (
            <div key={group.label}>
              <p className="text-[9px] font-medium uppercase tracking-[0.1em] text-[var(--text-tertiary)] mb-1 px-0.5">
                {group.label}
              </p>
              <div className="grid grid-cols-3 gap-1">
                {group.modes.map((m) => {
                  const isActive = mode === m.key;
                  return (
                    <button
                      key={m.key}
                      onClick={() => setMode(m.key)}
                      title={m.description}
                      className={`flex flex-col items-center gap-0.5 rounded-[var(--radius-md)] px-1 py-1.5 text-center transition-all ${
                        isActive
                          ? "bg-[var(--color-interactive-primary)]/12 text-[var(--color-interactive-primary)] ring-1 ring-[var(--color-interactive-primary)]/30"
                          : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-raised)]"
                      }`}
                    >
                      <m.icon
                        className={`h-3.5 w-3.5 ${isActive ? "" : "opacity-60"}`}
                      />
                      <span className="text-[10px] font-medium leading-none">
                        {m.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Calculator Body */}
        <div className="flex-1 overflow-y-auto p-3">
          {mode === "standard" && (
            <StandardCalc
              addHistory={addHistory}
              onApply={onApplyCallback ? handleApply : undefined}
            />
          )}
          {mode === "margin" && (
            <MarginCalc
              addHistory={addHistory}
              prefill={prefill}
              onApply={onApplyCallback ? handleApply : undefined}
            />
          )}
          {mode === "markup" && (
            <MarkupCalc
              addHistory={addHistory}
              prefill={prefill}
              onApply={onApplyCallback ? handleApply : undefined}
            />
          )}
          {mode === "profit" && (
            <ProfitCalc
              addHistory={addHistory}
              prefill={prefill}
              onApply={onApplyCallback ? handleApply : undefined}
            />
          )}
          {mode === "shipping" && (
            <ShippingCalc
              addHistory={addHistory}
              prefill={prefill}
              onApply={onApplyCallback ? handleApply : undefined}
            />
          )}
          {mode === "commission" && (
            <CommissionCalc
              addHistory={addHistory}
              prefill={prefill}
              onApply={onApplyCallback ? handleApply : undefined}
            />
          )}
          {mode === "roi" && (
            <RoiCalc
              addHistory={addHistory}
              prefill={prefill}
              onApply={onApplyCallback ? handleApply : undefined}
            />
          )}
          {mode === "tax" && (
            <TaxCalc
              addHistory={addHistory}
              prefill={prefill}
              onApply={onApplyCallback ? handleApply : undefined}
            />
          )}
          {mode === "converter" && (
            <ConverterCalc
              addHistory={addHistory}
              prefill={prefill}
              onApply={onApplyCallback ? handleApply : undefined}
            />
          )}
        </div>

        {/* History */}
        {history.length > 0 && (
          <div className="border-t border-[var(--color-border)] px-3 py-2 max-h-28 overflow-y-auto">
            <p className="text-[9px] uppercase tracking-wider text-[var(--text-tertiary)] mb-1">
              History
            </p>
            <div className="space-y-0.5">
              {history
                .slice()
                .reverse()
                .slice(0, 5)
                .map((entry, i) => (
                  <div key={i} className="flex items-center gap-2 text-[11px]">
                    <span className="shrink-0 rounded bg-[var(--color-surface-tertiary)] px-1 py-0.5 text-[9px] font-medium text-[var(--text-tertiary)] uppercase">
                      {entry.mode.slice(0, 3)}
                    </span>
                    <span className="text-[var(--text-secondary)] truncate">
                      {entry.expression}
                    </span>
                    <span className="ml-auto shrink-0 font-semibold text-[var(--text-primary)]">
                      {entry.result}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ==========================================================================
// STANDARD CALCULATOR
// ==========================================================================

function StandardCalc({
  addHistory,
  onApply,
}: {
  addHistory: (e: HistoryEntry) => void;
  onApply?: (v: number) => void;
}) {
  const [display, setDisplay] = useState("0");
  const [prevValue, setPrevValue] = useState<number | null>(null);
  const [operation, setOperation] = useState<string | null>(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);

  const MAX_LEN = 12;

  const formatDisplay = (v: number): string => {
    if (!isFinite(v)) return v > 0 ? "Infinity" : "-Infinity";
    if (isNaN(v)) return "Error";
    const s = String(v);
    if (s.length <= MAX_LEN) return s;
    if (s.includes(".")) {
      const parts = s.split(".");
      const intPart = parts[0] ?? "";
      const decPart = parts[1] ?? "";
      const avail = MAX_LEN - intPart.length - 1;
      if (avail > 0) return v.toFixed(Math.min(avail, decPart.length));
    }
    let p = 5;
    while (p >= 0) {
      const e = v.toExponential(p);
      if (e.length <= MAX_LEN) return e;
      p--;
    }
    return v.toExponential(0);
  };

  const inputNumber = (num: string) => {
    if (waitingForOperand) {
      setDisplay(num);
      setWaitingForOperand(false);
    } else {
      const nd = display === "0" ? num : display + num;
      if (nd.replace(".", "").replace("-", "").length <= MAX_LEN)
        setDisplay(nd);
    }
  };

  const calc = (a: number, b: number, op: string): number => {
    switch (op) {
      case "+":
        return a + b;
      case "-":
        return a - b;
      case "×":
        return a * b;
      case "÷":
        return a / b;
      default:
        return b;
    }
  };

  const inputOp = (nextOp: string) => {
    const val = parseFloat(display);
    if (prevValue === null) {
      setPrevValue(val);
    } else if (operation) {
      const res = calc(prevValue, val, operation);
      setDisplay(formatDisplay(res));
      setPrevValue(res);
      addHistory({
        mode: "standard",
        expression: `${prevValue} ${operation} ${val}`,
        result: formatDisplay(res),
      });
    }
    setWaitingForOperand(true);
    setOperation(nextOp);
  };

  const performCalc = () => {
    const val = parseFloat(display);
    if (prevValue !== null && operation) {
      const res = calc(prevValue, val, operation);
      setDisplay(formatDisplay(res));
      addHistory({
        mode: "standard",
        expression: `${prevValue} ${operation} ${val}`,
        result: formatDisplay(res),
      });
      setPrevValue(null);
      setOperation(null);
      setWaitingForOperand(true);
    }
  };

  const clear = () => {
    setDisplay("0");
    setPrevValue(null);
    setOperation(null);
    setWaitingForOperand(false);
  };
  const backspace = () =>
    setDisplay(display.length > 1 ? display.slice(0, -1) : "0");

  const handleBtn = (btn: string) => {
    if (btn === "C") clear();
    else if (btn === "=") performCalc();
    else if (["÷", "×", "-", "+"].includes(btn)) inputOp(btn);
    else if (btn === "±") setDisplay(formatDisplay(parseFloat(display) * -1));
    else if (btn === "%") setDisplay(formatDisplay(parseFloat(display) / 100));
    else if (btn === ".") {
      if (!display.includes(".")) inputNumber(btn);
    } else inputNumber(btn);
  };

  const btnClass = (btn: string) =>
    ["÷", "×", "-", "+", "="].includes(btn)
      ? "bg-brand-600 hover:bg-brand-700 text-white"
      : ["C", "±", "%"].includes(btn)
        ? "bg-[var(--color-surface-tertiary)] hover:bg-[var(--color-surface-hover)] text-[var(--text-primary)]"
        : "bg-[var(--color-surface-secondary)] hover:bg-[var(--color-surface-hover)] text-[var(--text-primary)]";

  return (
    <div>
      {/* Display */}
      <div className="rounded-[var(--radius-lg)] bg-[var(--color-surface-secondary)] border border-[var(--color-border)] p-3 mb-3">
        <div className="text-right text-2xl font-mono font-bold text-[var(--text-primary)] overflow-hidden">
          {display}
        </div>
        {operation && (
          <div className="text-right text-xs text-brand-600">
            {prevValue} {operation}
          </div>
        )}
      </div>

      {/* Buttons */}
      <div className="grid grid-cols-4 gap-1.5">
        {[
          "C",
          "±",
          "%",
          "÷",
          "7",
          "8",
          "9",
          "×",
          "4",
          "5",
          "6",
          "-",
          "1",
          "2",
          "3",
          "+",
        ].map((btn) => (
          <button
            key={btn}
            onClick={() => handleBtn(btn)}
            className={`${btnClass(btn)} h-10 rounded-[var(--radius-md)] text-sm font-semibold transition-all active:scale-95`}
          >
            {btn}
          </button>
        ))}
        <button
          onClick={() => handleBtn("0")}
          className={`${btnClass("0")} h-10 rounded-[var(--radius-md)] text-sm font-semibold transition-all active:scale-95 col-span-2`}
        >
          0
        </button>
        <button
          onClick={() => handleBtn(".")}
          className={`${btnClass(".")} h-10 rounded-[var(--radius-md)] text-sm font-semibold transition-all active:scale-95`}
        >
          .
        </button>
        <button
          onClick={() => handleBtn("=")}
          className={`${btnClass("=")} h-10 rounded-[var(--radius-md)] text-sm font-semibold transition-all active:scale-95`}
        >
          =
        </button>
      </div>

      {/* Backspace */}
      <div className="mt-2 flex justify-between items-center">
        <div />
        <button
          onClick={backspace}
          className="flex items-center gap-1 text-[11px] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
        >
          <Delete className="h-3 w-3" /> Backspace
        </button>
      </div>
      {onApply && display !== "0" && !isNaN(parseFloat(display)) && (
        <ApplyButton value={parseFloat(display)} onApply={onApply} />
      )}
    </div>
  );
}

// ==========================================================================
// FIELD INPUT HELPER
// ==========================================================================

function CalcField({
  label,
  value,
  onChange,
  prefix,
  suffix,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  prefix?: string;
  suffix?: string;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-[11px] font-medium text-[var(--text-secondary)] mb-1">
        {label}
      </label>
      <div className="flex items-center rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-secondary)] overflow-hidden">
        {prefix && (
          <span className="px-2 text-xs text-[var(--text-tertiary)] bg-[var(--color-surface-tertiary)]">
            {prefix}
          </span>
        )}
        <input
          type="number"
          step="any"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 h-8 px-2 text-sm bg-transparent text-[var(--text-primary)] outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
        {suffix && (
          <span className="px-2 text-xs text-[var(--text-tertiary)] bg-[var(--color-surface-tertiary)]">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

function ResultRow({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-xs text-[var(--text-secondary)]">{label}</span>
      <span
        className={`text-sm font-semibold ${highlight ? "text-brand-600" : "text-[var(--text-primary)]"}`}
      >
        {value}
      </span>
    </div>
  );
}

function CalcButton({
  onClick,
  label,
}: {
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full h-9 rounded-[var(--radius-md)] bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold transition-all active:scale-[0.98]"
    >
      {label}
    </button>
  );
}

function ApplyButton({
  value,
  onApply,
  label,
}: {
  value: number;
  onApply: (v: number) => void;
  label?: string;
}) {
  return (
    <button
      onClick={() => onApply(value)}
      className="w-full h-8 mt-2 rounded-[var(--radius-md)] bg-[var(--color-interactive-primary)] hover:opacity-90 text-white text-xs font-semibold transition-all active:scale-[0.98] flex items-center justify-center gap-1.5 shadow-sm"
    >
      Apply {label || fmt(value)} to field
    </button>
  );
}

// ==========================================================================
// MARGIN CALCULATOR
// ==========================================================================

function MarginCalc({
  addHistory,
  prefill = {},
  onApply,
}: {
  addHistory: (e: HistoryEntry) => void;
  prefill?: Record<string, string>;
  onApply?: (v: number) => void;
}) {
  const [cost, setCost] = useState(prefill.cost || "");
  const [margin, setMargin] = useState(prefill.margin || "");
  const [price, setPrice] = useState(prefill.price || "");
  const [result, setResult] = useState<{
    salePrice: number;
    profit: number;
    marginPct: number;
  } | null>(null);

  const calcFromMargin = () => {
    const c = parseFloat(cost);
    const m = parseFloat(margin);
    if (!c || !m) return;
    const sp = c / (1 - m / 100);
    const profit = sp - c;
    setResult({ salePrice: sp, profit, marginPct: m });
    setPrice(sp.toFixed(2));
    addHistory({
      mode: "margin",
      expression: `${fmt(c)} @ ${m}% margin`,
      result: fmt(sp),
    });
  };

  const calcFromPrice = () => {
    const c = parseFloat(cost);
    const p = parseFloat(price);
    if (!c || !p) return;
    const m = ((p - c) / p) * 100;
    setResult({ salePrice: p, profit: p - c, marginPct: m });
    setMargin(m.toFixed(2));
    addHistory({
      mode: "margin",
      expression: `${fmt(c)} → ${fmt(p)}`,
      result: fmtPct(m),
    });
  };

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-[var(--text-tertiary)]">
        Calculate sale price from desired margin, or find margin from known
        price.
      </p>
      <CalcField
        label="Cost (COGS)"
        value={cost}
        onChange={setCost}
        prefix="$"
        placeholder="10000"
      />
      <CalcField
        label="Desired Margin"
        value={margin}
        onChange={setMargin}
        suffix="%"
        placeholder="25"
      />
      <CalcButton
        onClick={calcFromMargin}
        label="Calculate Price from Margin"
      />
      <div className="flex items-center gap-2">
        <div className="flex-1 border-t border-[var(--color-border)]" />
        <span className="text-[10px] text-[var(--text-tertiary)]">OR</span>
        <div className="flex-1 border-t border-[var(--color-border)]" />
      </div>
      <CalcField
        label="Known Sale Price"
        value={price}
        onChange={setPrice}
        prefix="$"
        placeholder="13333"
      />
      <CalcButton onClick={calcFromPrice} label="Calculate Margin from Price" />
      {result && (
        <div className="rounded-[var(--radius-lg)] bg-[var(--color-surface-secondary)] border border-[var(--color-border)] p-3 mt-2">
          <ResultRow
            label="Sale Price"
            value={fmt(result.salePrice)}
            highlight
          />
          <ResultRow label="Profit" value={fmt(result.profit)} />
          <ResultRow
            label="Margin"
            value={fmtPct(result.marginPct)}
            highlight
          />
          {onApply && (
            <ApplyButton
              value={result.salePrice}
              onApply={onApply}
              label={fmt(result.salePrice)}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ==========================================================================
// MARKUP CALCULATOR
// ==========================================================================

function MarkupCalc({
  addHistory,
  prefill = {},
  onApply,
}: {
  addHistory: (e: HistoryEntry) => void;
  prefill?: Record<string, string>;
  onApply?: (v: number) => void;
}) {
  const [cost, setCost] = useState(prefill.cost || "");
  const [markupPct, setMarkupPct] = useState(prefill.markupPct || "");
  const [result, setResult] = useState<{
    price: number;
    profit: number;
    margin: number;
  } | null>(null);

  const calculate = () => {
    const c = parseFloat(cost);
    const m = parseFloat(markupPct);
    if (!c || isNaN(m)) return;
    const p = c * (1 + m / 100);
    const profit = p - c;
    const margin = (profit / p) * 100;
    setResult({ price: p, profit, margin });
    addHistory({
      mode: "markup",
      expression: `${fmt(c)} + ${m}% markup`,
      result: fmt(p),
    });
  };

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-[var(--text-tertiary)]">
        Apply a markup percentage to cost to get the sale price.
      </p>
      <CalcField
        label="Cost (COGS)"
        value={cost}
        onChange={setCost}
        prefix="$"
        placeholder="10000"
      />
      <CalcField
        label="Markup"
        value={markupPct}
        onChange={setMarkupPct}
        suffix="%"
        placeholder="30"
      />
      <CalcButton onClick={calculate} label="Calculate" />
      {result && (
        <div className="rounded-[var(--radius-lg)] bg-[var(--color-surface-secondary)] border border-[var(--color-border)] p-3">
          <ResultRow label="Sale Price" value={fmt(result.price)} highlight />
          <ResultRow label="Profit" value={fmt(result.profit)} />
          <ResultRow label="Effective Margin" value={fmtPct(result.margin)} />
          {onApply && (
            <ApplyButton
              value={result.price}
              onApply={onApply}
              label={fmt(result.price)}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ==========================================================================
// PROFIT CALCULATOR
// ==========================================================================

function ProfitCalc({
  addHistory,
  prefill = {},
  onApply,
}: {
  addHistory: (e: HistoryEntry) => void;
  prefill?: Record<string, string>;
  onApply?: (v: number) => void;
}) {
  const [salePrice, setSalePrice] = useState(prefill.salePrice || "");
  const [purchaseCost, setPurchaseCost] = useState(prefill.purchaseCost || "");
  const [reconCost, setReconCost] = useState(prefill.reconCost || "");
  const [shippingCost, setShippingCost] = useState(prefill.shippingCost || "");
  const [otherCosts, setOtherCosts] = useState(prefill.otherCosts || "");
  const [result, setResult] = useState<{
    totalCogs: number;
    grossProfit: number;
    margin: number;
    roi: number;
  } | null>(null);

  const calculate = () => {
    const sp = parseFloat(salePrice) || 0;
    const purchase = parseFloat(purchaseCost) || 0;
    const recon = parseFloat(reconCost) || 0;
    const ship = parseFloat(shippingCost) || 0;
    const other = parseFloat(otherCosts) || 0;
    const totalCogs = purchase + recon + ship + other;
    const gross = sp - totalCogs;
    const margin = sp > 0 ? (gross / sp) * 100 : 0;
    const roi = totalCogs > 0 ? (gross / totalCogs) * 100 : 0;
    setResult({ totalCogs, grossProfit: gross, margin, roi });
    addHistory({
      mode: "profit",
      expression: `${fmt(sp)} − ${fmt(totalCogs)} COGS`,
      result: fmt(gross),
    });
  };

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-[var(--text-tertiary)]">
        Full profit breakdown with itemized COGS.
      </p>
      <CalcField
        label="Sale Price"
        value={salePrice}
        onChange={setSalePrice}
        prefix="$"
        placeholder="25000"
      />
      <CalcField
        label="Purchase Cost"
        value={purchaseCost}
        onChange={setPurchaseCost}
        prefix="$"
        placeholder="15000"
      />
      <CalcField
        label="Reconditioning"
        value={reconCost}
        onChange={setReconCost}
        prefix="$"
        placeholder="3000"
      />
      <CalcField
        label="Shipping / Freight"
        value={shippingCost}
        onChange={setShippingCost}
        prefix="$"
        placeholder="500"
      />
      <CalcField
        label="Other Costs"
        value={otherCosts}
        onChange={setOtherCosts}
        prefix="$"
        placeholder="0"
      />
      <CalcButton onClick={calculate} label="Calculate Profit" />
      {result && (
        <div className="rounded-[var(--radius-lg)] bg-[var(--color-surface-secondary)] border border-[var(--color-border)] p-3">
          <ResultRow label="Total COGS" value={fmt(result.totalCogs)} />
          <div className="border-t border-[var(--color-border)] my-1" />
          <ResultRow
            label="Gross Profit"
            value={fmt(result.grossProfit)}
            highlight
          />
          <ResultRow label="Margin" value={fmtPct(result.margin)} />
          <ResultRow label="ROI" value={fmtPct(result.roi)} />
          {onApply && (
            <ApplyButton
              value={result.grossProfit}
              onApply={onApply}
              label={fmt(result.grossProfit)}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ==========================================================================
// SHIPPING SPLITTER
// ==========================================================================

function ShippingCalc({
  addHistory,
  prefill = {},
  onApply,
}: {
  addHistory: (e: HistoryEntry) => void;
  prefill?: Record<string, string>;
  onApply?: (v: number) => void;
}) {
  const [totalShipping, setTotalShipping] = useState(
    prefill.totalShipping || "",
  );
  const [items, setItems] = useState(prefill.items || "");
  const [itemValues, setItemValues] = useState("");
  const [result, setResult] = useState<{
    perItem: number;
    proportional: { value: number; share: number }[];
  } | null>(null);

  const calculate = () => {
    const total = parseFloat(totalShipping) || 0;
    const count = parseInt(items) || 0;
    if (!total || !count) return;

    const perItem = total / count;

    // Proportional split if item values provided.
    const values = itemValues
      .split(",")
      .map((v) => parseFloat(v.trim()))
      .filter((v) => !isNaN(v));
    const totalValue = values.reduce((s, v) => s + v, 0);
    const proportional =
      values.length > 0 && totalValue > 0
        ? values.map((v) => ({ value: v, share: (v / totalValue) * total }))
        : [];

    setResult({ perItem, proportional });
    addHistory({
      mode: "shipping",
      expression: `${fmt(total)} ÷ ${count} items`,
      result: fmt(perItem),
    });
  };

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-[var(--text-tertiary)]">
        Distribute shipping costs evenly or proportionally by item value.
      </p>
      <CalcField
        label="Total Shipping Cost"
        value={totalShipping}
        onChange={setTotalShipping}
        prefix="$"
        placeholder="1200"
      />
      <CalcField
        label="Number of Items"
        value={items}
        onChange={setItems}
        placeholder="4"
      />
      <div>
        <label className="block text-[11px] font-medium text-[var(--text-secondary)] mb-1">
          Item Values (optional, comma-separated)
        </label>
        <input
          value={itemValues}
          onChange={(e) => setItemValues(e.target.value)}
          placeholder="15000, 20000, 10000, 5000"
          className="w-full h-8 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-secondary)] px-2 text-sm text-[var(--text-primary)] outline-none"
        />
      </div>
      <CalcButton onClick={calculate} label="Calculate" />
      {result && (
        <div className="rounded-[var(--radius-lg)] bg-[var(--color-surface-secondary)] border border-[var(--color-border)] p-3">
          <ResultRow
            label="Even Split (per item)"
            value={fmt(result.perItem)}
            highlight
          />
          {result.proportional.length > 0 && (
            <>
              <div className="border-t border-[var(--color-border)] my-1.5" />
              <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] mb-1">
                Proportional Split
              </p>
              {result.proportional.map((item, i) => (
                <ResultRow
                  key={i}
                  label={`Item ${i + 1} (${fmt(item.value)})`}
                  value={fmt(item.share)}
                />
              ))}
            </>
          )}
          {onApply && (
            <ApplyButton
              value={result.perItem}
              onApply={onApply}
              label={fmt(result.perItem)}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ==========================================================================
// COMMISSION CALCULATOR
// ==========================================================================

function CommissionCalc({
  addHistory,
  prefill = {},
  onApply,
}: {
  addHistory: (e: HistoryEntry) => void;
  prefill?: Record<string, string>;
  onApply?: (v: number) => void;
}) {
  const [saleAmount, setSaleAmount] = useState(prefill.saleAmount || "");
  const [rate, setRate] = useState(prefill.rate || "");
  const [basis, setBasis] = useState<"sale" | "profit">("sale");
  const [cogs, setCogs] = useState("");
  const [result, setResult] = useState<{
    commission: number;
    netToCompany: number;
    basisAmount: number;
  } | null>(null);

  const calculate = () => {
    const sale = parseFloat(saleAmount) || 0;
    const r = parseFloat(rate) || 0;
    const c = parseFloat(cogs) || 0;
    const basisAmount = basis === "profit" ? sale - c : sale;
    const commission = basisAmount * (r / 100);
    const net = sale - commission;
    setResult({ commission, netToCompany: net, basisAmount });
    addHistory({
      mode: "commission",
      expression: `${fmt(basisAmount)} × ${r}%`,
      result: fmt(commission),
    });
  };

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-[var(--text-tertiary)]">
        Calculate salesperson commission on sale price or gross profit.
      </p>
      <CalcField
        label="Sale Amount"
        value={saleAmount}
        onChange={setSaleAmount}
        prefix="$"
        placeholder="50000"
      />
      <CalcField
        label="Commission Rate"
        value={rate}
        onChange={setRate}
        suffix="%"
        placeholder="5"
      />
      <div>
        <label className="block text-[11px] font-medium text-[var(--text-secondary)] mb-1">
          Basis
        </label>
        <div className="flex gap-2">
          {(["sale", "profit"] as const).map((b) => (
            <button
              key={b}
              onClick={() => setBasis(b)}
              className={`flex-1 h-8 rounded-[var(--radius-md)] text-xs font-medium transition-all ${
                basis === b
                  ? "bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300 border border-brand-300 dark:border-brand-700"
                  : "bg-[var(--color-surface-secondary)] text-[var(--text-tertiary)] border border-[var(--color-border)]"
              }`}
            >
              {b === "sale" ? "Sale Price" : "Gross Profit"}
            </button>
          ))}
        </div>
      </div>
      {basis === "profit" && (
        <CalcField
          label="COGS"
          value={cogs}
          onChange={setCogs}
          prefix="$"
          placeholder="30000"
        />
      )}
      <CalcButton onClick={calculate} label="Calculate" />
      {result && (
        <div className="rounded-[var(--radius-lg)] bg-[var(--color-surface-secondary)] border border-[var(--color-border)] p-3">
          <ResultRow
            label={basis === "profit" ? "Gross Profit (basis)" : "Sale (basis)"}
            value={fmt(result.basisAmount)}
          />
          <ResultRow
            label="Commission"
            value={fmt(result.commission)}
            highlight
          />
          <ResultRow label="Net to Company" value={fmt(result.netToCompany)} />
          {onApply && (
            <ApplyButton
              value={result.commission}
              onApply={onApply}
              label={fmt(result.commission)}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ==========================================================================
// ROI CALCULATOR
// ==========================================================================

function RoiCalc({
  addHistory,
  prefill = {},
  onApply,
}: {
  addHistory: (e: HistoryEntry) => void;
  prefill?: Record<string, string>;
  onApply?: (v: number) => void;
}) {
  const [invested, setInvested] = useState(prefill.invested || "");
  const [returned, setReturned] = useState(prefill.returned || "");
  const [result, setResult] = useState<{
    profit: number;
    roi: number;
    multiplier: number;
  } | null>(null);

  const calculate = () => {
    const inv = parseFloat(invested) || 0;
    const ret = parseFloat(returned) || 0;
    if (!inv) return;
    const profit = ret - inv;
    const roi = (profit / inv) * 100;
    const mult = ret / inv;
    setResult({ profit, roi, multiplier: mult });
    addHistory({
      mode: "roi",
      expression: `${fmt(ret)} on ${fmt(inv)} invested`,
      result: fmtPct(roi),
    });
  };

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-[var(--text-tertiary)]">
        Calculate return on investment for a deal or asset flip.
      </p>
      <CalcField
        label="Total Invested (all-in cost)"
        value={invested}
        onChange={setInvested}
        prefix="$"
        placeholder="18500"
      />
      <CalcField
        label="Total Return (sale price)"
        value={returned}
        onChange={setReturned}
        prefix="$"
        placeholder="25000"
      />
      <CalcButton onClick={calculate} label="Calculate ROI" />
      {result && (
        <div className="rounded-[var(--radius-lg)] bg-[var(--color-surface-secondary)] border border-[var(--color-border)] p-3">
          <ResultRow label="Profit" value={fmt(result.profit)} />
          <ResultRow label="ROI" value={fmtPct(result.roi)} highlight />
          <ResultRow
            label="Multiplier"
            value={`${result.multiplier.toFixed(2)}×`}
          />
          {onApply && (
            <ApplyButton
              value={result.profit}
              onApply={onApply}
              label={fmt(result.profit)}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ==========================================================================
// TAX CALCULATOR
// ==========================================================================

function TaxCalc({
  addHistory,
  prefill = {},
  onApply,
}: {
  addHistory: (e: HistoryEntry) => void;
  prefill?: Record<string, string>;
  onApply?: (v: number) => void;
}) {
  const [subtotal, setSubtotal] = useState(prefill.subtotal || "");
  const [taxRate, setTaxRate] = useState(prefill.taxRate || "");
  const [result, setResult] = useState<{ tax: number; total: number } | null>(
    null,
  );

  const calculate = () => {
    const sub = parseFloat(subtotal) || 0;
    const rate = parseFloat(taxRate) || 0;
    const tax = sub * (rate / 100);
    const total = sub + tax;
    setResult({ tax, total });
    addHistory({
      mode: "tax",
      expression: `${fmt(sub)} + ${rate}% tax`,
      result: fmt(total),
    });
  };

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-[var(--text-tertiary)]">
        Add tax to a subtotal to get the total amount.
      </p>
      <CalcField
        label="Subtotal"
        value={subtotal}
        onChange={setSubtotal}
        prefix="$"
        placeholder="45000"
      />
      <CalcField
        label="Tax Rate"
        value={taxRate}
        onChange={setTaxRate}
        suffix="%"
        placeholder="7.25"
      />
      <CalcButton onClick={calculate} label="Calculate Total" />
      {result && (
        <div className="rounded-[var(--radius-lg)] bg-[var(--color-surface-secondary)] border border-[var(--color-border)] p-3">
          <ResultRow label="Tax Amount" value={fmt(result.tax)} />
          <ResultRow label="Total" value={fmt(result.total)} highlight />
          {onApply && (
            <ApplyButton
              value={result.tax}
              onApply={onApply}
              label={`${fmt(result.tax)} tax`}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ==========================================================================
// CONVERTER
// ==========================================================================

type ConvCategory =
  | "currency"
  | "weight"
  | "length"
  | "area"
  | "volume"
  | "pressure"
  | "temperature"
  | "power";

interface UnitDef {
  key: string;
  label: string;
  abbr: string;
}

const CONV_CATEGORIES: { key: ConvCategory; label: string }[] = [
  { key: "currency", label: "Currency" },
  { key: "weight", label: "Weight" },
  { key: "length", label: "Length" },
  { key: "area", label: "Area" },
  { key: "volume", label: "Volume" },
  { key: "pressure", label: "Pressure" },
  { key: "temperature", label: "Temp" },
  { key: "power", label: "Power" },
];

const UNITS: Record<ConvCategory, UnitDef[]> = {
  currency: [],
  weight: [
    { key: "lb", label: "Pounds", abbr: "lb" },
    { key: "kg", label: "Kilograms", abbr: "kg" },
    { key: "ton_short", label: "Short Tons", abbr: "ton" },
    { key: "ton_metric", label: "Metric Tons", abbr: "t" },
  ],
  length: [
    { key: "in", label: "Inches", abbr: "in" },
    { key: "ft", label: "Feet", abbr: "ft" },
    { key: "yd", label: "Yards", abbr: "yd" },
    { key: "m", label: "Meters", abbr: "m" },
    { key: "cm", label: "Centimeters", abbr: "cm" },
    { key: "mm", label: "Millimeters", abbr: "mm" },
  ],
  area: [
    { key: "acre", label: "Acres", abbr: "ac" },
    { key: "hectare", label: "Hectares", abbr: "ha" },
    { key: "sqft", label: "Square Feet", abbr: "sq ft" },
    { key: "sqm", label: "Square Meters", abbr: "sq m" },
  ],
  volume: [
    { key: "gal", label: "Gallons (US)", abbr: "gal" },
    { key: "L", label: "Liters", abbr: "L" },
    { key: "qt", label: "Quarts", abbr: "qt" },
    { key: "cuft", label: "Cubic Feet", abbr: "cu ft" },
  ],
  pressure: [
    { key: "psi", label: "PSI", abbr: "PSI" },
    { key: "bar", label: "Bar", abbr: "bar" },
    { key: "kpa", label: "Kilopascals", abbr: "kPa" },
    { key: "atm", label: "Atmospheres", abbr: "atm" },
  ],
  temperature: [
    { key: "F", label: "Fahrenheit", abbr: "°F" },
    { key: "C", label: "Celsius", abbr: "°C" },
  ],
  power: [
    { key: "hp", label: "Horsepower", abbr: "HP" },
    { key: "kw", label: "Kilowatts", abbr: "kW" },
  ],
};

// Factors: value_in_base = input * FACTOR[unit]. Temperature handled separately.
const FACTORS: Record<string, Record<string, number>> = {
  weight: { lb: 1, kg: 2.20462, ton_short: 2000, ton_metric: 2204.62 },
  length: { in: 1, ft: 12, yd: 36, m: 39.3701, cm: 0.393701, mm: 0.0393701 },
  area: { sqft: 1, sqm: 10.7639, acre: 43560, hectare: 107639 },
  volume: { gal: 1, L: 0.264172, qt: 0.25, cuft: 7.48052 },
  pressure: { psi: 1, bar: 14.5038, kpa: 0.145038, atm: 14.696 },
  power: { hp: 1, kw: 1.34102 },
};

function convertUnit(
  category: ConvCategory,
  from: string,
  to: string,
  value: number,
): number {
  if (from === to) return value;
  if (category === "temperature") {
    if (from === "F" && to === "C") return (value - 32) * (5 / 9);
    if (from === "C" && to === "F") return value * (9 / 5) + 32;
    return value;
  }
  const factors = FACTORS[category];
  if (!factors) return value;
  const fromFactor = factors[from] ?? 1;
  const toFactor = factors[to] ?? 1;
  return (value * fromFactor) / toFactor;
}

const CURRENCIES = [
  "USD",
  "EUR",
  "GBP",
  "CAD",
  "AUD",
  "MXN",
  "BRL",
  "JPY",
  "CNY",
  "INR",
  "CHF",
  "SEK",
  "NZD",
  "ZAR",
  "AED",
];

function ConverterCalc({
  addHistory,
  prefill = {},
  onApply,
}: {
  addHistory: (e: HistoryEntry) => void;
  prefill?: Record<string, string>;
  onApply?: (v: number) => void;
}) {
  const [category, setCategory] = useState<ConvCategory>(
    (prefill.category as ConvCategory) || "currency",
  );
  const [inputVal, setInputVal] = useState("");
  const [fromUnit, setFromUnit] = useState("");
  const [toUnit, setToUnit] = useState("");

  // Currency-specific state
  const [currencyResult, setCurrencyResult] = useState<{
    converted: number;
    rate: number;
    date: string;
  } | null>(null);
  const [currencyError, setCurrencyError] = useState("");
  const [currencyLoading, setCurrencyLoading] = useState(false);

  const units = useMemo(() => UNITS[category], [category]);

  // Initialize from/to when category changes
  useEffect(() => {
    setCurrencyResult(null);
    setCurrencyError("");
    if (category === "currency") {
      setFromUnit("USD");
      setToUnit("EUR");
    } else if (units.length >= 2) {
      setFromUnit(units[0]!.key);
      setToUnit(units[1]!.key);
    }
  }, [category, units]);

  const swap = () => {
    setFromUnit(toUnit);
    setToUnit(fromUnit);
    setCurrencyResult(null);
  };

  const unitResult = useMemo(() => {
    if (category === "currency" || !inputVal) return null;
    const val = parseFloat(inputVal);
    if (isNaN(val)) return null;
    return convertUnit(category, fromUnit, toUnit, val);
  }, [category, inputVal, fromUnit, toUnit]);

  // Auto-add to history when unit conversion result changes
  useEffect(() => {
    if (unitResult === null || category === "currency") return;
    const fromAbbr = units.find((u) => u.key === fromUnit)?.abbr ?? fromUnit;
    const toAbbr = units.find((u) => u.key === toUnit)?.abbr ?? toUnit;
    const formatted =
      Math.abs(unitResult) >= 0.01 || unitResult === 0
        ? unitResult.toFixed(4).replace(/\.?0+$/, "")
        : unitResult.toExponential(4);
    addHistory({
      mode: "conv",
      expression: `${inputVal} ${fromAbbr} → ${toAbbr}`,
      result: `${formatted} ${toAbbr}`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitResult]);

  const convertCurrency = async () => {
    const amount = parseFloat(inputVal);
    if (isNaN(amount) || amount <= 0) return;
    setCurrencyLoading(true);
    setCurrencyError("");
    setCurrencyResult(null);
    try {
      const resp = await apiClient.post("/api/v1/exchange-rates/convert", {
        amount,
        source_currency: fromUnit,
        target_currency: toUnit,
      });
      const d = resp.data;
      setCurrencyResult({
        converted: d.converted_amount,
        rate: d.rate_used,
        date: d.rate_date,
      });
      addHistory({
        mode: "conv",
        expression: `${amount} ${fromUnit} → ${toUnit}`,
        result: `${Number(d.converted_amount).toFixed(2)} ${toUnit}`,
      });
    } catch {
      setCurrencyError(
        `No exchange rate found for ${fromUnit} → ${toUnit}. Record one on the Exchange Rates page.`,
      );
    } finally {
      setCurrencyLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Category tabs */}
      <div className="flex flex-wrap gap-1">
        {CONV_CATEGORIES.map((c) => (
          <button
            key={c.key}
            onClick={() => setCategory(c.key)}
            className={`rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors ${
              category === c.key
                ? "bg-[var(--color-interactive-primary)] text-white"
                : "bg-[var(--surface-sunken)] text-[var(--text-secondary)] hover:bg-[var(--surface-raised)]"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <p className="text-[11px] text-[var(--text-tertiary)]">
        {category === "currency"
          ? "Convert between currencies using stored exchange rates."
          : `Convert ${category} units.`}
      </p>

      {/* Value input */}
      <CalcField
        label="Value"
        value={inputVal}
        onChange={setInputVal}
        placeholder="Enter amount"
      />

      {/* From / Swap / To */}
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <label className="block text-[11px] font-medium text-[var(--text-secondary)] mb-1">
            From
          </label>
          <select
            value={fromUnit}
            onChange={(e) => {
              setFromUnit(e.target.value);
              setCurrencyResult(null);
            }}
            className="w-full h-8 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-secondary)] px-2 text-sm text-[var(--text-primary)]"
          >
            {category === "currency"
              ? CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))
              : units.map((u) => (
                  <option key={u.key} value={u.key}>
                    {u.abbr}
                  </option>
                ))}
          </select>
        </div>

        <button
          onClick={swap}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-secondary)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
          title="Swap"
        >
          <ArrowLeftRight className="h-3.5 w-3.5" />
        </button>

        <div className="flex-1">
          <label className="block text-[11px] font-medium text-[var(--text-secondary)] mb-1">
            To
          </label>
          <select
            value={toUnit}
            onChange={(e) => {
              setToUnit(e.target.value);
              setCurrencyResult(null);
            }}
            className="w-full h-8 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-secondary)] px-2 text-sm text-[var(--text-primary)]"
          >
            {category === "currency"
              ? CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))
              : units.map((u) => (
                  <option key={u.key} value={u.key}>
                    {u.abbr}
                  </option>
                ))}
          </select>
        </div>
      </div>

      {/* Currency: convert button */}
      {category === "currency" && (
        <CalcButton
          onClick={convertCurrency}
          label={currencyLoading ? "Converting…" : "Convert"}
        />
      )}

      {/* Currency result */}
      {category === "currency" && currencyResult && (
        <div className="rounded-[var(--radius-lg)] bg-[var(--color-surface-secondary)] border border-[var(--color-border)] p-3">
          <ResultRow
            label={`${inputVal} ${fromUnit}`}
            value={`${Number(currencyResult.converted).toFixed(2)} ${toUnit}`}
            highlight
          />
          <p className="text-[10px] text-[var(--text-tertiary)] mt-1">
            Rate: {Number(currencyResult.rate).toFixed(6)}
            {currencyResult.date &&
              ` · As of ${new Date(currencyResult.date).toLocaleDateString()}`}
          </p>
          {onApply && (
            <ApplyButton
              value={currencyResult.converted}
              onApply={onApply}
              label={`${Number(currencyResult.converted).toFixed(2)} ${toUnit}`}
            />
          )}
        </div>
      )}

      {/* Currency error */}
      {category === "currency" && currencyError && (
        <p className="text-xs text-[var(--text-danger)]">{currencyError}</p>
      )}

      {/* Unit result (live, no button needed) */}
      {category !== "currency" && unitResult !== null && (
        <div className="rounded-[var(--radius-lg)] bg-[var(--color-surface-secondary)] border border-[var(--color-border)] p-3">
          <ResultRow
            label={`${inputVal} ${units.find((u) => u.key === fromUnit)?.abbr ?? fromUnit}`}
            value={`${Math.abs(unitResult) >= 0.01 || unitResult === 0 ? unitResult.toFixed(4).replace(/\.?0+$/, "") : unitResult.toExponential(4)} ${units.find((u) => u.key === toUnit)?.abbr ?? toUnit}`}
            highlight
          />
          {onApply && (
            <ApplyButton
              value={unitResult}
              onApply={onApply}
              label={`${unitResult.toFixed(4).replace(/\.?0+$/, "")} ${units.find((u) => u.key === toUnit)?.abbr ?? toUnit}`}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default ResellerCalculator;
